import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Summary } from '../lib/api.js';
import { formatDate, useI18n, yen } from '../i18n.js';
import { Bars, Card, DivisionBadge, Empty, Notice, Stat } from '../components/ui.js';

/**
 * ダッシュボード。
 * 知りたいのは「いま在庫がいくらあるか」「どれだけ売れたか」の 2 つなので、
 * まず HIT / MSC それぞれの在庫金額と売却実績を並べ、そのあと内訳を出す。
 */
export function DashboardPage() {
  const { t, lang } = useI18n();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .summary()
      .then(setSummary)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) return <Notice kind="bad">{error}</Notice>;
  if (!summary) return <Empty>{t('loading')}</Empty>;

  const pick = (division: string, status: string) =>
    summary.byDivision.find((r) => r.division === division && r.status === status);

  const divisions = ['HIT', 'MSC'] as const;
  const hasData = summary.byDivision.length > 0;

  const totalInStock = summary.byDivision
    .filter((r) => r.status === 'in_stock')
    .reduce((a, r) => a + r.purchaseTotal, 0);
  const totalInStockCount = summary.byDivision
    .filter((r) => r.status === 'in_stock')
    .reduce((a, r) => a + r.count, 0);

  // 月別グラフ用に HIT/MSC をまとめる
  const monthly = new Map<string, { purchased: number; sold: number }>();
  for (const r of summary.purchasedByMonth) {
    const cur = monthly.get(r.month) ?? { purchased: 0, sold: 0 };
    cur.purchased += r.count;
    monthly.set(r.month, cur);
  }
  for (const r of summary.soldByMonth) {
    const cur = monthly.get(r.month) ?? { purchased: 0, sold: 0 };
    cur.sold += r.count;
    monthly.set(r.month, cur);
  }
  const recentMonths = [...monthly.entries()].sort((a, b) => a[0].localeCompare(b[0])).slice(-12);

  return (
    <>
      <h1 className="page-title">{t('dashboard')}</h1>
      <p className="page-sub">
        {summary.lastImport ? (
          <>
            {t('lastImport')}: {summary.lastImport.filename} ·{' '}
            {t('snapshotDate')} {formatDate(summary.lastImport.snapshot_date, lang)}
          </>
        ) : (
          <Link to="/import">{t('uploadTitle')} →</Link>
        )}
      </p>

      {!hasData && (
        <Notice kind="warn">
          まだ在庫リストが取り込まれていません。<Link to="/import">{t('import')}</Link> から最初のファイルを入れてください。
        </Notice>
      )}

      {summary.saleMissingCount > 0 && (
        <Notice kind="warn">
          {t('saleMissing')}: {summary.saleMissingCount} ·{' '}
          <Link to="/sold?saleMissing=1">{t('sold')} →</Link>
        </Notice>
      )}

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <Stat
          label={`${t('inStockValue')} (${t('all')})`}
          value={yen(totalInStock)}
          sub={`${totalInStockCount} ${t('vehicles')}`}
        />
        {divisions.map((d) => {
          const inStock = pick(d, 'in_stock');
          const sold = pick(d, 'sold');
          return (
            <Stat
              key={d}
              label={
                <>
                  <DivisionBadge division={d} /> {t('inStockValue')}
                </>
              }
              value={yen(inStock?.purchaseTotal ?? 0)}
              sub={
                <>
                  {inStock?.count ?? 0} {t('vehicles')} · {t('soldStatus')} {sold?.count ?? 0}
                </>
              }
            />
          );
        })}
        {/*
          売上と粗利は「売却済み」の行だけで集計する。
          在庫に戻した車に前回の売却価格が残っていても、売上に混ざらないようにするため。
        */}
        {(() => {
          const soldRows = summary.byDivision.filter((r) => r.status === 'sold');
          const revenue = soldRows.reduce((a, r) => a + r.saleTotal, 0);
          const cost = soldRows.reduce((a, r) => a + r.pricedCost, 0);
          const priced = soldRows.reduce((a, r) => a + r.priced, 0);
          return (
            <Stat
              label={t('revenue')}
              value={yen(revenue)}
              sub={
                priced === 0 ? (
                  t('notEntered')
                ) : (
                  <>
                    {t('profit')} {yen(revenue - cost)} · {priced} {t('vehicles')}
                  </>
                )
              }
            />
          );
        })()}
      </div>

      <div className="grid cols-2">
        <Card title={`${t('byMaker')} · ${t('inStock')}`}>
          <Bars
            data={summary.makers.map((m) => ({
              name: m.key,
              value: m.count,
              display: `${m.count} · ${yen(m.purchaseTotal)}`,
            }))}
          />
        </Card>

        <Card title={`${t('bySupplier')} · ${t('inStock')}`}>
          <Bars
            data={summary.suppliers.map((m) => ({
              name: m.key,
              value: m.count,
              display: `${m.count} · ${yen(m.purchaseTotal)}`,
            }))}
          />
        </Card>

        <Card title={`${t('byModel')} · ${t('inStock')}`}>
          <Bars
            data={summary.models.map((m) => ({
              name: m.key,
              value: m.count,
              display: `${m.count} · ${yen(m.purchaseTotal)}`,
            }))}
          />
        </Card>

        <Card title={`${t('purchasedByMonth')} / ${t('soldByMonth')}`}>
          {recentMonths.length === 0 ? (
            <div className="faint">—</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>—</th>
                  <th className="num">{t('purchasedByMonth')}</th>
                  <th className="num">{t('soldByMonth')}</th>
                </tr>
              </thead>
              <tbody>
                {recentMonths.map(([month, v]) => (
                  <tr key={month}>
                    <td>{month}</td>
                    <td className="num">{v.purchased || '—'}</td>
                    <td className="num">{v.sold || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {summary.oldestInStock.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Card title={t('oldest')}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('division')}</th>
                    <th>{t('maker')}</th>
                    <th>{t('model')}</th>
                    <th>{t('chassis')}</th>
                    <th>{t('purchaseDate')}</th>
                    <th className="num">{t('purchasePrice')}</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.oldestInStock.map((row) => {
                    const r = row as {
                      id: number;
                      division: string;
                      chassis_raw: string;
                      maker: string;
                      model: string;
                      purchase_date: string;
                      purchase_price: number;
                    };
                    return (
                      <tr key={r.id}>
                        <td>
                          <DivisionBadge division={r.division} />
                        </td>
                        <td>{r.maker}</td>
                        <td>
                          <Link to={`/vehicles/${r.id}`}>{r.model || '—'}</Link>
                        </td>
                        <td className="mono">{r.chassis_raw || '—'}</td>
                        <td className="muted">{formatDate(r.purchase_date, lang)}</td>
                        <td className="num">{yen(r.purchase_price)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
